<template>
    <div v-if="svgContent" ref="currentElRef" class="svg-container" v-html="svgContent" />
</template>

<script lang="ts" setup>
import { Ref, nextTick, onMounted, ref } from 'vue';

const props = withDefaults(
    defineProps<{
        src: string;
        hasFill?: boolean;
        growByHeight?: boolean;
    }>(),
    {
        hasFill: false,
        growByHeight: true
    }
);

const svgContent: Ref<string | null> = ref(null);
const currentElRef: Ref<HTMLElement | null> = ref(null);

onMounted(async () => {
    await nextTick();
    const response = await fetch(props.src);
    svgContent.value = await response.text();
    await nextTick();
    buildSvg();
});

const recursivelyRemoveFill = (el: Element) => {
    if (!el) {
        return;
    }

    el.removeAttribute('fill');
    [].forEach.call(el.children, (child) => {
        recursivelyRemoveFill(child);
    });
};

const buildSvg = () => {
    const svgElement = currentElRef.value?.firstElementChild;
    if (svgElement && svgElement.nodeName === 'svg') {
        const viewBoxAttr = svgElement.getAttribute('viewBox');

        if (viewBoxAttr) {
            // Use `viewBox` attribute to get the svg's inherent width and height
            const viewBox = viewBoxAttr.split(' ').map((n) => Number(n));

            const widthToHeight = (viewBox[2] / viewBox[3]).toFixed(2);
            if (props.hasFill) {
                // Recursively remove all fill attribute of element and its nested children
                recursivelyRemoveFill(svgElement);
            }

            // Set width and height relative to font size if growByHeight is true, height set to 1em else width set to 1em and remaining is calculated based on widthToHeight ratio
            if (props.growByHeight) {
                svgElement.setAttribute('height', '1em');
                svgElement.setAttribute('width', `${widthToHeight}em`);
            } else {
                svgElement.setAttribute('width', '1em');
                svgElement.setAttribute('height', `${1 / Number(widthToHeight)}em`);
            }
            svgElement.classList.add('svg-class');
        }
    }
};
</script>

<style lang="scss" scoped>
.svg-container {
    display: inline-flex;
    align-items: center;
}
</style>
